// #959 — News Crawler detail view (global).
// v1.1.0 (#977) adds the engagement-gate summary header.
//
// Top: gate summary (#977) — users-with-interests vs how many are due
// for a search right now. Then cost header (total + this-month).
// Bottom: paginated searches grid across all users. Click a
// user UUID -> their per-user news-topics view.
//
// The per-user equivalent of this view is AdminUserNewsTopics.js
// (#960) — both share NewsCrawlerSearchesPanel.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';
import NewsCrawlerSearchesPanel from './NewsCrawlerSearchesPanel';

const VERSION = 'v1.1.0';
const PAGE_SIZE = 100;

export default function AdminCronNewsCrawler() {
  const [cost, setCost] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [searches, setSearches] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await adminFetch(
          `/api/admin/crons/news-crawler?limit=${PAGE_SIZE}&offset=0`,
        );
        if (cancelled) return;
        setCost(r?.cost || null);
        setEligibility(r?.eligibility || null);
        setSearches(r?.searches || []);
        setHasMore(!!r?.has_more);
        setOffset(PAGE_SIZE);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function loadMore() {
    try {
      const r = await adminFetch(
        `/api/admin/crons/news-crawler?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      setSearches(prev => prev.concat(r?.searches || []));
      setHasMore(!!r?.has_more);
      setOffset(offset + PAGE_SIZE);
    } catch (e) {
      setError(e.message || 'Failed to load more');
    }
  }

  return (
    <AdminLayout title="News Crawler">
      <style>{userSubPageStyles}</style>

      <Link to="/admin/crons" className="back-link">← Back to Crons</Link>

      <div className="page-header">
        <div className="page-title">
          News Crawler<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Daily 03:00 UTC engagement-gated pre-warm of cached_briefings · cost + per-search audit
        </div>
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && eligibility && (
        <div style={{
          display: 'flex', gap: 24, flexWrap: 'wrap',
          padding: '12px 16px', margin: '4px 0 12px',
          background: '#F7F4F0', border: '1px solid #EDE9E4',
          borderRadius: 10,
        }}>
          <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#333' }}>
              {eligibility.eligible_now}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#888' }}>
              due for a search now
            </div>
          </div>
          <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#333' }}>
              {eligibility.users_with_interests}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#888' }}>
              users with interests
            </div>
          </div>
          <div style={{ alignSelf: 'center', color: '#666', fontSize: '0.85rem' }}>
            Gate: called since last search · {eligibility.interval_days}-day interval
            or cards depleted. Never-callers cost nothing.
          </div>
        </div>
      )}

      {!loading && !error && (
        <NewsCrawlerSearchesPanel
          cost={cost}
          searches={searches}
          hasMore={hasMore}
          onLoadMore={loadMore}
          showUserColumn={true}
        />
      )}
    </AdminLayout>
  );
}
